// "Bearbeiten" on one proposal a meeting filed: the title, the
// body trio (description, outcome, acceptance criteria) and the Ticket/Epic
// switch.
//
// - Opens with the issue as the forge has it now (`row.body`, parsed by the
//   one issue-body parser the server writes through). Meeting text on the
//   forge is neutralised (`` `` @x `` ``); the form shows it plain
//   (`stripForgeNeutraliser`) and the server neutralises every field again on
//   the way back, so nothing the user did not touch changes.
// - Sends only what changed (`diffDraftPatch`, the dashboard's own rule): the
//   trio travels together or not at all, a blank title is never sent, and a
//   class switch rides alone when nothing else moved.
// - Ctrl/Cmd+Enter saves from any field; a plain Enter is a newline
//   (memory: multiline fields submit on Ctrl/Cmd+Enter only).

import { useMemo, useState, type KeyboardEvent } from "react";

import { stripForgeNeutraliser } from "@loom/shared/issue-body";
import type {
  MeetingIssuePatchRequest,
  MeetingIssueRowDto,
  TicketClass,
} from "@loom/shared/meetings";

import { diffDraftPatch, parseBody, type IssueDraftFields } from "../../dashboard/issueDraft";
import { useT } from "../../i18n/index.js";
import { AutoGrowTextarea } from "../../ui/AutoGrowTextarea";

/** The form's starting values, from the row the server sent. */
export const editBaselineOf = (row: MeetingIssueRowDto): IssueDraftFields => {
  const parsed = parseBody(row.body ?? null);
  return {
    title: stripForgeNeutraliser(row.title ?? ""),
    description: stripForgeNeutraliser(parsed.description),
    outcome: stripForgeNeutraliser(parsed.outcome),
    acceptanceCriteria: parsed.acceptanceCriteria.map(stripForgeNeutraliser),
    // Not on this form: a priority is only ever what someone said in the
    // meeting, and the dashboard edits it. Never sent from here.
    priority: "normal",
  };
};

/** What a Save sends, or null when nothing changed. */
export const editPatchOf = (
  draft: IssueDraftFields,
  baseline: IssueDraftFields,
  kind: TicketClass,
  baselineKind: TicketClass,
): MeetingIssuePatchRequest | null => {
  const patch = diffDraftPatch(draft, baseline);
  const switched = kind !== baselineKind;
  if (patch === null && !switched) return null;
  return { ...(patch ?? {}), ...(switched ? { class: kind } : {}) };
};

const isSaveChord = (e: KeyboardEvent): boolean =>
  e.key === "Enter" && (e.metaKey || e.ctrlKey);

export function MeetingIssueEdit({
  row,
  busy,
  onSave,
  onCancel,
}: {
  readonly row: MeetingIssueRowDto;
  readonly busy: boolean;
  readonly onSave: (patch: MeetingIssuePatchRequest) => void;
  readonly onCancel: () => void;
}) {
  const t = useT();
  const baseline = useMemo(() => editBaselineOf(row), [row]);
  const [draft, setDraft] = useState<IssueDraftFields>(baseline);
  const [criteria, setCriteria] = useState(baseline.acceptanceCriteria.join("\n"));
  const [kind, setKind] = useState<TicketClass>(row.class);

  const current: IssueDraftFields = { ...draft, acceptanceCriteria: criteria.split("\n") };
  const patch = editPatchOf(current, baseline, kind, row.class);

  const save = (): void => {
    if (busy || patch === null) return;
    onSave(patch);
  };
  const onKeyDown = (e: KeyboardEvent): void => {
    if (!isSaveChord(e)) return;
    e.preventDefault();
    save();
  };

  return (
    <div className="meet-edit" onKeyDown={onKeyDown}>
      <label className="meet-edit-field">
        <span className="meet-edit-label">{t("meetings.edit.title")}</span>
        <input
          className="meet-edit-input"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        />
      </label>
      <label className="meet-edit-field">
        <span className="meet-edit-label">{t("meetings.edit.description")}</span>
        <AutoGrowTextarea
          className="meet-edit-input"
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        />
      </label>
      {kind === "ticket" && (
        <>
          <label className="meet-edit-field">
            <span className="meet-edit-label">{t("meetings.edit.outcome")}</span>
            <AutoGrowTextarea
              className="meet-edit-input"
              value={draft.outcome}
              onChange={(e) => setDraft({ ...draft, outcome: e.target.value })}
            />
          </label>
          <label className="meet-edit-field">
            <span className="meet-edit-label">{t("meetings.edit.criteria")}</span>
            <AutoGrowTextarea
              className="meet-edit-input"
              value={criteria}
              onChange={(e) => setCriteria(e.target.value)}
            />
          </label>
        </>
      )}
      <div className="meet-edit-field" role="radiogroup" aria-label={t("meetings.edit.kind")}>
        <span className="meet-edit-label">{t("meetings.edit.kind")}</span>
        <div className="meet-actions">
          {(["ticket", "epic"] as const).map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={kind === k}
              className={`focus-chip${kind === k ? " is-on" : ""}`}
              onClick={() => setKind(k)}
            >
              {t(`meetings.class.${k}`)}
            </button>
          ))}
        </div>
      </div>
      <div className="meet-actions">
        <button
          type="button"
          className="focus-btn"
          disabled={busy || patch === null}
          onClick={save}
        >
          {t("common.save")}
        </button>
        <button type="button" className="focus-btn is-ghost" disabled={busy} onClick={onCancel}>
          {t("common.cancel")}
        </button>
        <span className="focus-enter-hint">{t("meetings.edit.saveHint")}</span>
      </div>
    </div>
  );
}
